/**
 * Pianificazione (M2) — quattro sezioni invece di quattordici destinazioni.
 *
 * Fino al 17-08 era una pagina sola con 5 tab e, dentro Impostazioni, altre 9
 * sotto-tab: quattordici posti, nessuno raggiungibile dalla barra. Adesso segue
 * la grammatica già approvata su Commesse e Spedizioni:
 *
 *  - **Cruscotto** (atterraggio) — come sta il piano, fatto di porte;
 *  - **Piano** — la lista di lavoro, con i riquadri che sono i filtri;
 *  - **Simulazione** — il what-if e il riordino dell'AI;
 *  - **Fabbisogno** — cosa c'è da produrre e cosa non si riesce a calcolare;
 *  - **Impostazioni** — tre gruppi di manopole.
 *
 * Le sezioni sono voci di barra (`SEZIONI_M2` in `nav-config.ts`) e vivono
 * nell'URL: `?tab=` è l'indirizzo, condivisibile. Via il blocco titolo con la
 * sigla — la barra dice già dove sei (decisione di Enrico, 16-08).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Upload } from "lucide-react";

import { GuidaModulo } from "@/components/GuidaModulo";
import { PageContainer } from "@/components/PageContainer";
import { CHAT_AGENTE_W } from "@/components/agenti/ChatAgente";
import { CaricaMrpDialog } from "@/components/m2/CaricaMrpDialog";
import { CaricaPianoDialog } from "@/components/m2/CaricaPianoDialog";
import { CaricamentiM2 } from "@/components/m2/CaricamentiM2";
import { ElsaDock } from "@/components/m2/ElsaDock";
import { CruscottoDirezione } from "@/components/m2/CruscottoDirezione";
import { CruscottoM2 } from "@/components/m2/CruscottoM2";
import { FabbisognoM2 } from "@/components/m2/FabbisognoM2";
import { ImpostazioniM2 } from "@/components/m2/ImpostazioniM2";
import { ImpostazioniOverlay } from "@/components/ImpostazioniOverlay";
import { PianoM2 } from "@/components/m2/PianoM2";
import { PianoPropostoM2 } from "@/components/m2/PianoPropostoM2";
import { TabModulo } from "@/components/TabModulo";
import { useMyPermessi } from "@/hooks/usePermessi";
import { SEZIONI_M2 } from "@/lib/nav-config";
import { canUploadDati, hasPermessoSezione } from "@/lib/permessi";
import { SimulazioneM2 } from "@/components/m2/SimulazioneM2";
import { useM2Piano, useM2PianoProposto } from "@/hooks/useM2";
import type { PianoResponse } from "@/types/m2";

type Sezione = "cruscotto" | "piano" | "caricamenti" | "simulazione" | "fabbisogno";

/**
 * Gli indirizzi vecchi restano validi: `ottimizza` e `whatif` erano due tab e
 * ora sono la stessa sezione, `evadibilita` è finita nel Fabbisogno. Un link in
 * un appunto o in una mail non deve rompersi per un ridisegno.
 */
const ALIAS: Record<string, Sezione> = {
  cruscotto: "cruscotto",
  piano: "piano",
  caricamenti: "caricamenti",
  simulazione: "simulazione",
  whatif: "simulazione",
  ottimizza: "simulazione",
  fabbisogno: "fabbisogno",
  evadibilita: "fabbisogno",
};

export function M2PianificazionePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const sezione: Sezione = ALIAS[tabParam ?? ""] ?? "cruscotto";
  // Le Impostazioni sono uscite dalle tab (Enrico, 27-08): sono configurazione,
  // non un posto dove si lavora. `?tab=impostazioni` resta valido — è l'indirizzo
  // dei link vecchi e della voce in barra — e apre il pannello sopra il modulo.
  const impostazioniDaUrl = tabParam === "impostazioni";
  // Il filtro del Piano vive nell'URL, come in Commesse: le porte del Cruscotto
  // ci scrivono, e arrivando a pagina già montata il filtro si applica comunque.
  const filtro = searchParams.get("filtro");

  const { data: piano, isLoading } = useM2Piano();
  const [caricaAperto, setCaricaAperto] = useState(false);
  const [caricaMrpAperto, setCaricaMrpAperto] = useState(false);
  const [elsaAperta, setElsaAperta] = useState(false);
  const [impostazioniAperte, setImpostazioniAperte] = useState(impostazioniDaUrl);
  // Chi ha il modulo ha anche le manopole, salvo un «No» esplicito sulla sezione:
  // è lo stesso permesso che governava la tab, quindi nessuno perde niente.
  const { data: perms } = useMyPermessi();
  const puoImpostazioni = hasPermessoSezione(perms, "m2.impostazioni", "M2");
  // ⚠️ Entrare nel modulo e **caricare** sono due permessi diversi
  // ([[module_dati_caricamenti]]): Fabio ha M2 e non ha `upload_dati`, quindi
  // fino al 19-09-2026 vedeva i pulsanti di caricamento e prendeva 403 dopo aver
  // scelto il file. `canUploadDati` esisteva già in `permessi.ts` e non era usata
  // da nessuna parte. `null` = il pulsante non c'è, invece di esserci e mentire.
  const puoCaricare = canUploadDati(perms);
  const apriCaricaPiano = puoCaricare ? () => setCaricaAperto(true) : null;
  const apriCaricaMrp = puoCaricare ? () => setCaricaMrpAperto(true) : null;

  function vai(s: Sezione, f?: string) {
    const u = new URLSearchParams(searchParams);
    if (s === "cruscotto") u.delete("tab");
    else u.set("tab", s);
    if (f) u.set("filtro", f);
    else u.delete("filtro");
    setSearchParams(u, { replace: true });
  }

  // Cambiando sezione la finestra torna in cima: le sezioni sono pagine diverse,
  // e restare a metà scroll della precedente sembra un caricamento a metà.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [sezione]);

  return (
    <div className="flex min-h-0 flex-1">
      <div
        // `data-area` è un ancoraggio per la prova in browser: misurare una classe
        // di utilità (`.flex-1`) pescava un altro elemento e il controllo passava
        // per caso.
        data-area="contenuto-m2"
        className="min-w-0 flex-1"
        style={elsaAperta ? { maxWidth: `calc(100% - ${CHAT_AGENTE_W}px)` } : undefined}
      >
        <PageContainer className="space-y-4">
          <TabModulo
            sezioni={SEZIONI_M2}
            modulo="M2"
            ingranaggioVisibile={puoImpostazioni}
            onIngranaggio={() => setImpostazioniAperte(true)}
          />

          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Caricamento…
            </div>
          ) : sezione === "caricamenti" ? (
            /* Fuori dal guard sul piano MES di proposito: il fabbisogno è un
               input suo, e chi ha appena caricato l'MRP deve poterne vedere il
               referto anche in un ambiente dove il piano MES non c'è. */
            <CaricamentiM2 onCarica={apriCaricaMrp} />
          ) : !piano || !piano.piano_id ? (
            <NessunPiano onCarica={apriCaricaPiano} />
          ) : sezione === "cruscotto" ? (
            /* Dal blocco 3 il Cruscotto è la **vista d'apertura di Fabio**
               (§4.8): produzione contro piano, non più la salute del piano MES.
               Quella vecchia resta raggiungibile accanto al piano MES, finché
               non ritiriamo entrambi. */
            <CruscottoDirezione onVai={vai} />
          ) : sezione === "piano" ? (
            <SezionePiano
              piano={piano}
              filtroIniziale={filtro}
              onCarica={apriCaricaPiano}
              onVaiAiCaricamenti={() => vai("caricamenti")}
            />
          ) : sezione === "simulazione" ? (
            <SimulazioneM2 piano={piano} />
          ) : (
            <FabbisognoM2 />
          )}

          {/*
            La guida resta raggiungibile da ogni sezione. Il caricamento **no**:
            sta nella testata (e nell'avviso quando il piano è vecchio), e
            ripeterlo qui metteva lo stesso pulsante due volte nella stessa
            schermata — visto guardando la pagina.
          */}
          <div className="flex justify-end border-t pt-3">
            <GuidaModulo modulo="M2" />
          </div>
        </PageContainer>
      </div>

      {/*
        Elsa. Lo spazio era già stato previsto (inerte) qualche ora prima, quando
        l'agente non esisteva: si accende togliendo quel flag, e la pagina non
        cambia forma — che era esattamente lo scopo di prevederlo.
      */}
      <ElsaDock sezione={sezione} aperta={elsaAperta} onApertaChange={setElsaAperta} />

      <CaricaPianoDialog aperto={caricaAperto} onChiudi={() => setCaricaAperto(false)} />
      <CaricaMrpDialog aperto={caricaMrpAperto} onChiudi={() => setCaricaMrpAperto(false)} />

      {impostazioniAperte && (
        <ImpostazioniOverlay onChiudi={() => setImpostazioniAperte(false)}>
          <ImpostazioniM2 />
        </ImpostazioniOverlay>
      )}
    </div>
  );
}

function NessunPiano({ onCarica }: { onCarica: (() => void) | null }) {
  return (
    <div className="rounded-lg border border-dashed bg-muted/40 p-10 text-center">
      <p className="text-sm text-muted-foreground">
        Nessun piano di produzione caricato.
      </p>
      {onCarica && (
      <button
        type="button"
        onClick={onCarica}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        <Upload className="size-3.5" />
        Carica il piano MES
      </button>
      )}
    </div>
  );
}

/**
 * La sezione Piano nel passaggio da un input all'altro.
 *
 * Dal 14-09-2026 il piano lo **costruisce il modulo** dal fabbisogno: è quello
 * che si vede per primo. Il piano del MES resta raggiungibile finché non lo
 * ritiriamo, perché è ancora la fonte del Cruscotto e della Simulazione e
 * toglierlo dalla vista prima di aver spostato anche quelle vorrebbe dire far
 * sparire dei numeri che qualcuno sta ancora guardando.
 *
 * ⚠️ La riga che distingue i due non è un dettaglio: sono due piani diversi con
 * due origini diverse, e confonderli è esattamente il difetto che si voleva
 * evitare tenendoli in tabelle separate.
 */
function SezionePiano({
  piano,
  filtroIniziale,
  onCarica,
  onVaiAiCaricamenti,
}: {
  piano: PianoResponse;
  filtroIniziale?: string | null;
  onCarica: (() => void) | null;
  onVaiAiCaricamenti: () => void;
}) {
  const { data: proposto } = useM2PianoProposto();
  const [mostraMes, setMostraMes] = useState(false);
  const cEProposta = Boolean(proposto?.piano);

  if (mostraMes || !cEProposta) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/50 px-3.5 py-2.5 text-[12px]">
          <span className="text-muted-foreground">
            Stai guardando il <b>piano del MES</b>, che è la fonte in uscita: il modulo non lo
            costruisce, lo riceve già fatto.
          </span>
          {cEProposta && (
            <button
              type="button"
              onClick={() => setMostraMes(false)}
              className="ml-auto font-medium text-primary hover:opacity-80"
            >
              Vai alla proposta costruita dal fabbisogno
            </button>
          )}
        </div>
        <PianoM2 piano={piano} filtroIniziale={filtroIniziale} onCarica={onCarica} />
        <CruscottoM2 piano={piano} onVai={() => undefined} onCarica={onCarica} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PianoPropostoM2 onVaiAlFabbisogno={onVaiAiCaricamenti} />
      {piano.piano_id && (
        <div className="flex justify-end border-t pt-3">
          <button
            type="button"
            onClick={() => setMostraMes(true)}
            className="text-[12px] text-muted-foreground hover:text-foreground"
          >
            Vedi il vecchio piano del MES
          </button>
        </div>
      )}
    </div>
  );
}
